import { BlogPost } from "@/types/blog";

/**
 * Fetches blog posts from WordPress REST API with pagination support
 * @param apiUrl The WordPress URL (will be formatted to API endpoint)
 * @param progressCallback Optional callback for reporting progress
 * @returns Promise resolving to array of BlogPost objects
 */
export const fetchPostsFromWordPressApi = async (
  apiUrl: string, 
  progressCallback?: (message: string) => void
): Promise<BlogPost[]> => {
  try {
    // Clean up the URL
    let baseUrl = apiUrl.trim();
    
    // Enforce HTTPS - this is crucial to avoid CORS and mixed content issues
    if (!baseUrl.startsWith('https://')) {
      if (baseUrl.startsWith('http://')) {
        baseUrl = `https://${baseUrl.substring(7)}`;
      } else {
        baseUrl = `https://${baseUrl}`;
      }
      console.log("Converted URL to HTTPS:", baseUrl);
    }
    
    // Format the URL for WordPress API if needed
    if (!baseUrl.includes("/wp-json/")) {
      baseUrl = baseUrl.replace(/\/$/, ""); // Remove trailing slash if present
      baseUrl = `${baseUrl}/wp-json/wp/v2/posts`;
    }
    
    const allPosts: BlogPost[] = [];
    let currentPage = 1;
    let totalPages = 1;
    const perPage = 100; // Maximum allowed by WordPress API
    
    progressCallback?.("Connecting to WordPress API...");
    console.log(`Attempting to connect to WordPress API at: ${baseUrl}`);
    
    // First request to get total pages
    const initialResponse = await fetch(`/api/wordpress-proxy?url=${encodeURIComponent(baseUrl)}&page=1&per_page=${perPage}&_embed=true`, { 
      headers: { 
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      } 
    });
    
    if (!initialResponse.ok) {
      let errorMessage = "Failed to connect to WordPress API";
      let errorDetails = {};
      
      try {
        const errorData = await initialResponse.json();
        errorMessage = errorData.error || errorMessage;
        errorDetails = errorData;
        
        // Add details if available
        if (errorData.details) {
          console.error("Error details:", errorData.details);
        }
        
        // Include preview if HTML was detected
        if (errorData.preview) {
          console.error("HTML preview:", errorData.preview);
        }
        
        if (errorData.redirect_url) {
          console.error("Request was redirected to:", errorData.redirect_url);
        }
        
        console.error("WordPress API error details:", errorData);
        
        // If we received HTML instead of JSON, provide a more helpful error
        if (errorData.preview && typeof errorData.preview === 'string' && 
            (errorData.preview.toLowerCase().includes("<!doctype") || 
             errorData.preview.toLowerCase().includes("<html"))) {
          throw new Error(`${errorMessage}: The site returned HTML instead of JSON data. This might not be a WordPress site with the REST API enabled.${JSON.stringify(errorDetails)}`);
        }
        
      } catch (parseError) {
        // If we can't parse JSON, try to get text content
        try {
          const errorText = await initialResponse.text();
          if (errorText) {
            // Check if the response is HTML
            if (errorText.trim().toLowerCase().startsWith("<!doctype") || 
                errorText.includes("<html") || errorText.includes("<body")) {
              
              throw new Error(`${errorMessage}: The site returned HTML instead of JSON data. This might not be a WordPress site with the REST API enabled, or the site might be redirecting to a login page. (Status: ${initialResponse.status})`);
            }
            
            errorMessage += `. Response: ${errorText.substring(0, 100)}...`;
          }
        } catch (textError) {
          console.error("Error reading response text:", textError);
        }
        
        console.error("Error parsing error response:", parseError);
      }
      
      throw new Error(`${errorMessage} (Status: ${initialResponse.status})`);
    }
    
    try {
      const initialData = await initialResponse.json();
      
      if (!initialData.data || !Array.isArray(initialData.data)) {
        console.error("Invalid response format:", initialData);
        throw new Error("Invalid response format from WordPress API. Expected an array of posts.");
      }
      
      // Get pagination information
      totalPages = parseInt(initialData.headers["X-WP-TotalPages"]) || 1;
      const totalPosts = parseInt(initialData.headers["X-WP-Total"]) || initialData.data.length;
      
      console.log(`Found ${totalPosts} total posts across ${totalPages} pages`);
      progressCallback?.(`Found ${totalPosts} posts, fetching page 1 of ${totalPages}...`);
      
      // Process first page results
      const firstPagePosts = await processWordPressPosts(initialData.data, baseUrl);
      allPosts.push(...firstPagePosts);
      
      // Fetch all remaining pages if there are more
      if (totalPages > 1) {
        // Start from page 2 since we already have page 1
        for (let page = 2; page <= totalPages; page++) {
          progressCallback?.(`Fetching page ${page} of ${totalPages}...`);
          console.log(`Fetching page ${page} of ${totalPages}`);
          
          try {
            const pageResponse = await fetch(`/api/wordpress-proxy?url=${encodeURIComponent(baseUrl)}&page=${page}&per_page=${perPage}&_embed=true`);
            
            if (!pageResponse.ok) {
              console.error(`Error fetching page ${page}:`, pageResponse.statusText);
              continue; // Skip this page but continue with others
            }
            
            const pageData = await pageResponse.json();
            
            if (pageData.data && Array.isArray(pageData.data)) {
              const pagePosts = await processWordPressPosts(pageData.data, baseUrl);
              allPosts.push(...pagePosts);
            }
          } catch (error) {
            console.error(`Error processing page ${page}:`, error);
          }
          
          // Add a small delay to prevent overwhelming the server
          await new Promise(resolve => setTimeout(resolve, 300));
        }
      }
      
      console.log(`Successfully fetched ${allPosts.length} posts from WordPress`);
      progressCallback?.(`Successfully fetched ${allPosts.length} posts from WordPress`);
      
      return allPosts;
    } catch (jsonError) {
      console.error("Error parsing API response:", jsonError);
      
      // Check for HTML content in the error
      const errorText = jsonError.toString();
      if (errorText.includes('<!doctype') || errorText.includes('<html')) {
        throw new Error(`Received HTML instead of JSON. The site might not be a WordPress site or the REST API might be disabled.`);
      }
      
      throw jsonError;
    }
  } catch (error) {
    console.error("Error fetching WordPress posts:", error);
    throw error;
  }
};

/**
 * Processes raw WordPress posts into our BlogPost format
 * Includes handling of tags, categories, and featured images
 */
async function processWordPressPosts(wpPosts: any[], baseUrl: string): Promise<BlogPost[]> {
  const baseApiUrl = baseUrl.replace(/\/wp-json\/wp\/v2\/posts.*$/, "");
  const posts: BlogPost[] = [];
  
  for (const post of wpPosts) {
    try {
      // Preserve the original WordPress slug
      const originalSlug = post.slug || '';
      
      // Extract featured image - first try _embedded data which is more efficient
      let featuredImage = "";
      
      // Try to get the image from _embedded data
      if (post._embedded && post._embedded['wp:featuredmedia'] && 
          post._embedded['wp:featuredmedia'][0] && 
          post._embedded['wp:featuredmedia'][0].source_url) {
        featuredImage = post._embedded['wp:featuredmedia'][0].source_url;
        console.log(`Found embedded featured image for post "${post.title?.rendered}": ${featuredImage}`);
      } 
      // Fall back to fetching separately if not found in _embedded
      else if (post.featured_media) {
        try {
          console.log(`Fetching featured media ID ${post.featured_media} for post "${post.title?.rendered}"`);
          const mediaResponse = await fetch(`/api/wordpress-proxy?url=${encodeURIComponent(baseApiUrl)}&endpoint=media&id=${post.featured_media}`);
          if (mediaResponse.ok) {
            const mediaData = await mediaResponse.json();
            if (mediaData.data && mediaData.data.source_url) {
              featuredImage = mediaData.data.source_url || "";
              console.log(`Fetched featured image: ${featuredImage}`);
            } else {
              console.warn(`Media data missing source_url for media ID ${post.featured_media}`);
            }
          } else {
            console.error(`Failed to fetch media ID ${post.featured_media}: ${mediaResponse.status}`);
          }
        } catch (error) {
          console.error("Error fetching featured image:", error);
        }
      }
      
      // Extract categories - first try _embedded data
      const categories: string[] = [];
      
      if (post._embedded && post._embedded['wp:term']) {
        // Category terms are typically the first array in wp:term
        const categoryTerms = post._embedded['wp:term'].find((terms: any[]) => 
          terms.length > 0 && terms[0].taxonomy === 'category'
        ) || [];
        
        categoryTerms.forEach((term: any) => {
          if (term && term.name) {
            categories.push(term.name);
          }
        });
        
        if (categories.length > 0) {
          console.log(`Found ${categories.length} embedded categories for post "${post.title?.rendered}"`);
        }
      } 
      // Fall back to fetching separately
      else if (post.categories && post.categories.length > 0) {
        console.log(`Fetching ${post.categories.length} categories for post "${post.title?.rendered}"`);
        for (const catId of post.categories) {
          try {
            const catResponse = await fetch(`/api/wordpress-proxy?url=${encodeURIComponent(baseApiUrl)}&endpoint=categories&id=${catId}`);
            if (catResponse.ok) {
              const catData = await catResponse.json();
              if (catData.data && catData.data.name) {
                categories.push(catData.data.name);
              }
            }
          } catch (error) {
            console.error(`Error fetching category ${catId}:`, error);
          }
        }
      }
      
      // Extract tags - first try _embedded data
      const tags: string[] = [];
      
      if (post._embedded && post._embedded['wp:term']) {
        // Tag terms are typically the second array in wp:term
        const tagTerms = post._embedded['wp:term'].find((terms: any[]) => 
          terms.length > 0 && terms[0].taxonomy === 'post_tag'
        ) || [];
        
        tagTerms.forEach((term: any) => {
          if (term && term.name) {
            tags.push(term.name);
          }
        });
        
        if (tags.length > 0) {
          console.log(`Found ${tags.length} embedded tags for post "${post.title?.rendered}"`);
        }
      }
      // Fall back to fetching separately
      else if (post.tags && post.tags.length > 0) {
        console.log(`Fetching ${post.tags.length} tags for post "${post.title?.rendered}"`);
        for (const tagId of post.tags) {
          try {
            const tagResponse = await fetch(`/api/wordpress-proxy?url=${encodeURIComponent(baseApiUrl)}&endpoint=tags&id=${tagId}`);
            if (tagResponse.ok) {
              const tagData = await tagResponse.json();
              if (tagData.data && tagData.data.name) {
                tags.push(tagData.data.name);
              }
            }
          } catch (error) {
            console.error(`Error fetching tag ${tagId}:`, error);
          }
        }
      }
      
      // Clean excerpt from HTML tags
      const cleanExcerpt = post.excerpt?.rendered ? post.excerpt.rendered.replace(/<[^>]*>/g, "") : "";
      
      // Format date
      const originalDate = post.date;
      const date = new Date(post.date).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
      
      // Calculate reading time
      const content = post.content?.rendered || "";
      const wordCount = content.replace(/<[^>]*>/g, "").split(/\s+/).length;
      const readingTime = Math.max(1, Math.ceil(wordCount / 200));
      
      // Try to get author info from _embedded data
      let author = "Unknown";
      
      if (post._embedded && post._embedded.author && post._embedded.author[0]) {
        author = post._embedded.author[0].name || "Unknown";
        console.log(`Found embedded author for post "${post.title?.rendered}": ${author}`);
      } else if (post.author) {
        try {
          console.log(`Fetching author ID ${post.author} for post "${post.title?.rendered}"`);
          const authorResponse = await fetch(`/api/wordpress-proxy?url=${encodeURIComponent(baseApiUrl)}&endpoint=users&id=${post.author}`);
          if (authorResponse.ok) {
            const authorData = await authorResponse.json();
            if (authorData.data && authorData.data.name) {
              author = authorData.data.name;
              console.log(`Found author: ${author}`);
            }
          }
        } catch (error) {
          console.error(`Error fetching author for post "${post.title?.rendered}":`, error);
        }
      }
      
      posts.push({
        title: post.title?.rendered || "Untitled",
        slug: originalSlug,
        excerpt: cleanExcerpt.substring(0, 160),
        content: content,
        coverImage: featuredImage || undefined,
        date,
        author,
        tags: [...categories, ...tags],
        readingTime,
        wordpressId: post.id ? String(post.id) : undefined,
        originalDate
      });
    } catch (error) {
      console.error(`Error processing post ${post.id || 'unknown'}:`, error);
    }
  }
  
  return posts;
}
